import { useFormik } from 'formik'
import React, { ChangeEvent, useState } from 'react'
import ModalComponent from '..'
import { useModalControl } from '../../../hooks/useModalControl'
import {
  useCreateProductTagMutation,
  useEditProductTagMutation,
} from '../../../redux-toolkits/products/products.slice'
import { Tag } from '../../../redux-toolkits/products/products.type'
import { CreateTagSchema } from '../../../validation/validate'
import { Button } from '../../button'
import { ImageUploadButton } from '../../button/imageUploadButton'
import Icon from '../../icon'
import { ImageBanner } from '../../images/imageBanner'
import Dropdown from '../../inputs/dropdown'
import { EditInput } from '../../inputs/editInput'
import { TextInput } from '../../inputs/textInput'
import { ImageUploadPreviewModal } from './ImagePreviewModal'

const statuses = [
  { text: 'Active', value: true },
  { text: 'Inactive', value: false },
]

export const AddTagModal: React.FC<{
  open: boolean
  setOpen: (cal: boolean) => void
  onCloseCallback?: () => void
  onSuccessCallback?: () => void
  mode: 'edit' | 'create'
  tag?: Tag
}> = ({
  open,
  setOpen,
  onCloseCallback = () => {},
  onSuccessCallback = () => {},
  mode,
  tag,
}) => {
  const [uploadedIcon, setUploadedIcon] = useState<File>()
  const [editIndex, setEditIndex] = useState<number>()

  const [createTag, { isLoading: creating }] = useCreateProductTagMutation()
  const [editTag, { isLoading: editing }] = useEditProductTagMutation()

  const { modal, modalOpen, setModalOpen, handleModalOpen, handleModalClose } =
    useModalControl()

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) {
      return
    }
    const selectedFile = event.target.files[0]
    setUploadedIcon(selectedFile)
    handleModalOpen('imageUploadPreviewModal')
  }

  const {
    values,
    errors,
    handleSubmit,
    handleChange,
    handleBlur,
    touched,
    setFieldValue,
    isValid,
    resetForm,
  } = useFormik({
    initialValues: {
      icon: tag?.icon ?? '',
      name: tag?.name ?? '',
      description: tag?.description ?? '',
      isActive: tag?.isActive ?? false,
    },
    enableReinitialize: true,
    validationSchema: CreateTagSchema,
    onSubmit: ({ isActive, ...values }) => {
      if (mode === 'create') {
        createTag(values).then((resp) => {
          if (resp.data?.success) {
            resetForm()
            setOpen(false)
            onSuccessCallback()
          }
        })
      } else if (mode === 'edit' && tag) {
        editTag({
          id: tag?._id,
          payload: { ...values, isActive: isActive },
        }).then((resp) => {
          if (resp.data?.success) {
            resetForm()
            setOpen(false)
            onSuccessCallback()
          }
        })
      }
    },
  })
  return (
    <ModalComponent
      open={open}
      setOpen={setOpen}
      onCloseCallback={() => {
        resetForm()
        onCloseCallback()
      }}
      title={mode === 'create' ? 'Add Tag' : 'Edit Tag'}
      childrenClassName="md:!max-w-[420px]"
    >
      <form
        className="flex flex-col items-center justify-center gap-4"
        onSubmit={handleSubmit}
      >
        <>
          {values.icon ? (
            <ImageBanner
              imageUrl={values.icon}
              loading={false}
              className="
              !rounded-full
              !w-[120px] !max-h-[120px]
              !bg-contain
              "
              errors={errors.icon ? errors.icon : ''}
              onImageEdit={(e) => {
                handleFileChange(e)
              }}
            />
          ) : (
            <ImageUploadButton
              handleChange={(e) => {
                handleFileChange(e)
              }}
              showText
              icon={'pic-icon'}
              iconDim={52}
              text={`Upload tag svg`}
              className="
              !relative !w-full
              !m-0 !top-0 !right-0
              !h-fit
              !rounded-lg
              !col-span-full
              "
            />
          )}
        </>

        <EditInput
          title="Tag name"
          value={values.name}
          onChange={handleChange}
          type="text"
          placeholder="Tag name"
          onEditSave={() => {
            setEditIndex(undefined)
          }}
          editing={editIndex === 1}
          onEditClick={() => {
            setEditIndex(1)
          }}
          onCancel={() => {
            if (mode === 'create') {
              setFieldValue('name', '')
            } else {
              setFieldValue('name', tag?.name)
            }
            setEditIndex(undefined)
          }}
          loading={false}
          inputName="name"
          mode={mode === 'create' ? 'edit' : 'edit'}
        />

        <EditInput
          title="Tag description"
          value={values.description}
          onChange={handleChange}
          type="text"
          placeholder="Tag description"
          onEditSave={() => {
            setEditIndex(undefined)
          }}
          editing={editIndex === 2}
          onEditClick={() => {
            setEditIndex(2)
          }}
          onCancel={() => {
            if (mode === 'create') {
              setFieldValue('description', '')
            } else {
              setFieldValue('description', tag?.description)
            }
            setEditIndex(undefined)
          }}
          loading={false}
          inputName="description"
          mode={mode === 'create' ? 'edit' : 'edit'}
        />

        {mode === 'edit' && (
          <Dropdown
            className="w-full"
            menuClassName="!max-h-[200px]"
            position="bottom start"
            menuButton={
              <TextInput
                type="text"
                title="Status"
                name="status"
                placeholder="Select status"
                value={values.isActive ? 'Active' : 'Inactive'}
                onBlur={handleBlur}
                onChange={handleChange}
                hasIcon={false}
                readOnly
                extra={
                  <Icon
                    id={'left-caret'}
                    width={16}
                    height={16}
                    className="-rotate-90 text-sec-black"
                  />
                }
              />
            }
            menuItems={statuses.map((status) => {
              return {
                name: status.text,
                value: status.value,
                onClick: () => {
                  setFieldValue('isActive', status.value)
                },
              }
            })}
          />
        )}

        <Button
          label="Proceed"
          type="submit"
          className="mt-2 w-full"
          disabled={!isValid || creating || editing}
          loading={creating || editing}
          primary
        />
      </form>

      {uploadedIcon ? (
        <ImageUploadPreviewModal
          image={uploadedIcon as File}
          open={modal === 'imageUploadPreviewModal' && modalOpen}
          setOpen={setModalOpen}
          onCloseCallback={() => {
            setUploadedIcon(undefined)
          }}
          onSuccessCallback={(url) => {
            setFieldValue('icon', url)
          }}
        />
      ) : null}
    </ModalComponent>
  )
}
